import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Checkbox,
  Spinner,
} from '@ibsys/kit';
import type { HiddenCriterionRef } from '@/entities/dashboards';
import { criterionKey } from '../model/hiddenCriteria';
import { useCriteriaCatalog, type CriteriaCatalogGroup } from '../model/useCriteriaCatalog';

type Props = {
  /** Критерии, скрытые из отображения дашборда, — каждый в разрезе своего заключения. */
  hiddenCriteria: HiddenCriterionRef[];
  /** Новый полный список скрытых критериев после переключения. */
  onChange: (nextHidden: HiddenCriterionRef[]) => void;
};

/** Значение группового чекбокса по числу видимых критериев группы. */
const groupCheckboxValue = (group: CriteriaCatalogGroup, hiddenKeys: Set<string>): boolean | 'indeterminate' => {
  const hiddenCount = group.criteria.filter((name) => hiddenKeys.has(criterionKey(group.reportId, name))).length;
  if (hiddenCount === 0) return true;
  if (hiddenCount === group.criteria.length) return false;
  return 'indeterminate';
};

/**
 * Групповой пикер видимости критериев (без перетаскивания): полный каталог критериев всех
 * экспертных заключений, сгруппированный по отчётам-владельцам сворачиваемыми группами с выбором
 * всей группы разом. Снятая галочка добавляет критерий в `hiddenCriteria`, по которому дашборд
 * скрывает его из графика, «Красных зон», сводки и KPI.
 *
 * Критерий адресуется вместе со своим заключением: одноимённые критерии разных заключений —
 * разные критерии, и снятие галочки у одного из них не трогает галочку другого. Каталог берётся
 * из entity `expert-report`, а не из выборки дашборда, чтобы в настройках были и критерии,
 * которых в выбранном периоде нет.
 */
export const CriteriaVisibilityPicker: FC<Props> = ({ hiddenCriteria, onChange }) => {
  const { t } = useTranslation('qualityDashboard');
  const { groups, isLoading } = useCriteriaCatalog();

  const hiddenKeys = new Set(hiddenCriteria.map((item) => criterionKey(item.expertReportId, item.name)));

  // Показать критерии заключения — убрать их из скрытых; скрыть — добавить. Ключ (заключение +
  // имя) дедуплицирует набор и не даёт задеть одноимённый критерий другого заключения.
  const setVisibility = (reportId: number, names: string[], visible: boolean) => {
    const touched = new Set(names.map((name) => criterionKey(reportId, name)));
    const rest = hiddenCriteria.filter((item) => !touched.has(criterionKey(item.expertReportId, item.name)));
    if (visible) {
      onChange(rest);
      return;
    }
    onChange([...rest, ...names.map((name) => ({ expertReportId: reportId, name }))]);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-2">
        <Spinner className="size-4" />
      </div>
    );
  }

  if (groups.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('displaySettings.noCriteria')}</p>;
  }

  return (
    <Accordion type="multiple" className="flex flex-col">
      {groups.map((group) => (
        <AccordionItem key={group.reportId} value={String(group.reportId)} className="border-0">
          {/* Групповой чекбокс рядом с триггером, а не внутри него: чекбокс — интерактивный элемент,
              его нельзя вкладывать в кнопку-триггер аккордеона. */}
          <div className="flex items-center gap-2">
            <Checkbox
              value={groupCheckboxValue(group, hiddenKeys)}
              onChange={(value) => setVisibility(group.reportId, group.criteria, value === true)}
            />
            <AccordionTrigger className="flex-1 text-sm font-medium">{group.reportName}</AccordionTrigger>
          </div>
          <AccordionContent>
            <div className="flex flex-col gap-2 pl-6 pt-1">
              {group.criteria.map((name) => (
                <Checkbox
                  key={name}
                  label={name}
                  value={!hiddenKeys.has(criterionKey(group.reportId, name))}
                  onChange={(value) => setVisibility(group.reportId, [name], value === true)}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
